import React from "react";
import { useState, useEffect, useRef, useContext, useMemo } from "react";
import {
  fetchPages,
  getQuestion,
  updateTopic,
  updateTopicStatus,
  submitCourse,
  updateTotalTimeElapsed,
} from "../../services/api";
import { useNavigate } from "react-router-dom";
import { CourseContext } from "./CourseContext";
import { getItem, getJSON, setItem, setJSON, getUser } from "../utils/session";
import {
  SingleQuestionQuizScreen,
  QuizAlertScreen,
  LoadingScreen,
  NoPagesScreen,
  CourseContentScreen,
  WelcomeBackScreen,
  CourseLockedScreen,
  InitialWelcomeScreen,
  ChapterQuizPromptScreen
} from "./screens";


const Content = (props) => {
  const {
    pages,
    setPages,
    sections,
    topicstatus,
    topic,
    setTopic,
    currentSection,
    courseStatus,
    startcourse,
    setStartCourse,
    showSuccess,
    setShowSuccess,
    quizalert,
    setQuizalert,
    refreshSections,
    setSidebarlock,
    // Security Challenge state from global context
    totalTimeElapsed,
    setTotalTimeElapsed,
    breakpoint1,
    setBreakpoint1,
    breakpoint2,
    setBreakpoint2,
    showChallengeModal,
    setShowChallengeModal,
    askedQuestionIds,
  } = useContext(CourseContext) || {};

  // Debug: Log context values when they change
  useEffect(() => {
    console.log("[Content] Context values - breakpoint1:", breakpoint1, "breakpoint2:", breakpoint2, "totalTimeElapsed:", totalTimeElapsed, "setShowChallengeModal:", typeof setShowChallengeModal);
  }, [breakpoint1, breakpoint2, totalTimeElapsed, setShowChallengeModal]);

  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [useranswer, setUseranswer] = useState("");
  const [currentquiztime, setCurrentquiztime] = useState();
  const [backremainingTime, setBackRemainingTime] = useState(0);
  const [lock, setLock] = useState(false);
  const [timerShow, setTimerShow] = useState(false);
  const currentCounterTimeRef = useRef(0);
  // Store topic timer state when modal opens so we can resume from there
  const pausedTopicTimerRef = useRef(null);
  const [displayback , setDisplayback] = useState(courseStatus);
  const user = useMemo(() => getUser(), []);
  const isLastTopic = useMemo(() => getItem('lastTopic', '0') === '1', []);
  
  // Helper function to check if operations should be disabled
  // Operations are disabled if security_verification_status === '1' OR course_status === '2'
  const shouldDisableOperations = () => {
    const userData = getUser();
    const securityVerificationStatus = userData?.security_verification_status;
    const courseStatusValue = userData?.course_status;
    return securityVerificationStatus === '1' || courseStatusValue === '2';
  };
  
  // Check if current topic is the last chapter of the last section
  const isLastChapterOfLastSection = useMemo(() => {
    if (!sections || !topic) return false;
    
    const currentSectionIndex = sections.findIndex(
      (section) => section.course_section_id === topic.course_section_id
    );
    
    if (currentSectionIndex === -1) return false;
    
    const currentSection = sections[currentSectionIndex];
    const currentTopicIndex = currentSection.topics.findIndex(
      (t) => t.topics.id === topic.id
    );
    
    // Check if this is the last topic in the last section
    const isLastTopicInSection = currentTopicIndex === currentSection.topics.length - 1;
    const isLastSection = currentSectionIndex === sections.length - 1;
    
    return isLastTopicInSection && isLastSection;
  }, [sections, topic]);
  const [showquestion, setShowquestion] = useState(false);
  const [quizerror, setQuizError] = useState(false);
  const [startquiz, setStartQuiz] = useState(false);
  const [question, setQuestion] = useState();
  const [quizbutton, setQuizbutton] = useState(false);
  const [submitquizbutton, setSubmitquizButton] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showQuizExitModal, setShowQuizExitModal] = useState(false);
  const [showCourseExitModal, setShowCourseExitModal] = useState(false);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasError, setHasError] = useState(false);
  
  // Track which breakpoints have been asked in this session (for local tracking)
  const [askedBreakpoints, setAskedBreakpoints] = useState(() => {
    try {
      const stored = sessionStorage.getItem("asked_breakpoints");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  
  const navigate = useNavigate();

  // Persist askedBreakpoints to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("asked_breakpoints", JSON.stringify(askedBreakpoints));
  }, [askedBreakpoints]);

  // If a new attempt/retake restarts totalTimeElapsed from 0, allow breakpoints to trigger again
  // by clearing the local "already asked" tracking for breakpoints.
  const prevTotalTimeElapsedRef = useRef(totalTimeElapsed);
  useEffect(() => {
    const prev = prevTotalTimeElapsedRef.current;
    const curr = totalTimeElapsed;

    // Detect reset (e.g., backend sends 0 again on retake)
    if (
      curr != null &&
      Number.isFinite(Number(curr)) &&
      (Number(curr) === 0 || (prev != null && Number.isFinite(Number(prev)) && Number(curr) < Number(prev)))
    ) {
      if (askedBreakpoints.length > 0) {
        console.log("[Content] Detected totalTimeElapsed reset -> clearing askedBreakpoints");
        setAskedBreakpoints([]);
        sessionStorage.removeItem("asked_breakpoints");
      }
    }

    prevTotalTimeElapsedRef.current = curr;
  }, [totalTimeElapsed, askedBreakpoints.length]);

  useEffect(() => {
    const userRaw = sessionStorage.getItem('user');
    if (userRaw) {
      const courseStatus = JSON.parse(userRaw).course_status;
      if (courseStatus === '1') {
        setDisplayback(true);
      } else if (courseStatus === '2') {
        setLock(true);
      }
    }
    
    // Reset states when topic changes
    setCurrentPageIndex(0);
    setTimerShow(false);
    setHasError(false);
    setIsLoadingPages(false);
    
    if (topic && topic.id) {
      console.log('Loading pages for topic:', topic.id);
      getPages();
    } else {
      // If no topic, ensure we're not stuck in loading state
      setIsInitialLoad(false);
      setIsLoadingPages(false);
    }
    
    // Set initial load to false after first render
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [topic]);

  // Safety timeout to prevent infinite loading
  useEffect(() => {
    if (isInitialLoad) {
      const timeout = setTimeout(() => {
        setIsInitialLoad(false);
      }, 3000); // 3 second timeout
      
      return () => clearTimeout(timeout);
    }
  }, [isInitialLoad]);

  const getPages = async () => {
    const token = sessionStorage.getItem("token");
    const user_id = sessionStorage.getItem("user_id");
    const id = topic.id;
    
    if (!token || !user_id || !id) {
      console.error("Missing required data for getPages:", { token: !!token, user_id: !!user_id, id: !!id });
      setHasError(true);
      setIsLoadingPages(false);
      setIsTransitioning(false); // Clear transitioning to prevent stuck state
      return;
    }
    
    setIsLoadingPages(true);
    setHasError(false);
    
    try {
      console.log('Fetching pages for topic:', id);
      const response = await fetchPages(token, id, user_id);
      
      if (!response || !response.data) {
        throw new Error('Invalid response from fetchPages API');
      }
      
      // Set pages from the API response
      const pagesData = response.data.pages || [];
      setPages && setPages(pagesData);
      console.log('Pages loaded:', pagesData.length);
      
      const timeInSeconds = convertTimeStringToSeconds(topic.time);
      setTotalTime(timeInSeconds);
      
      // Handle progress and timer logic
      // CRITICAL: Don't modify timerShow if quiz is loading to prevent screen switching
      const progress = response.data.progress;
      const timeSpent = progress ? progress.time_spent : null;
      
      // Initialize totalTimeElapsed from backend if available (update global context)
      // Check both nested and direct paths to handle different API response structures
      const backendTotalTimeElapsed = response.data.data?.totalTimeElapsed ??null;
      if (backendTotalTimeElapsed != null && Number.isFinite(Number(backendTotalTimeElapsed))) {
        const elapsedSeconds = Number(backendTotalTimeElapsed);
        setTotalTimeElapsed && setTotalTimeElapsed(elapsedSeconds);
        sessionStorage.setItem("total_time_elapsed", String(elapsedSeconds));
        console.log("[Content] ✅ totalTimeElapsed initialized from backend:", elapsedSeconds, "seconds");
      } else {
        // If API doesn't return it, use the value from global context (which should be from Course.jsx)
        const contextTimeElapsed = totalTimeElapsed || 0;
        if (contextTimeElapsed > 0) {
          console.log("[Content] Using totalTimeElapsed from context:", contextTimeElapsed, "seconds");
        }
      }

      // Breakpoints are now managed in global context (Course.jsx), so we don't set them here
      
      // Only update timer state if quiz is NOT loading
      if (!(quizbutton && startquiz && !showquestion)) {
        if(progress && progress.status === '2'){
          setTimerShow(false);
        } else {
          // Check if course is completed (reading mode) or operations should be disabled
          const isCourseCompleted = courseStatus === '2' || (user && user.course_status === '2');
          const disableOps = shouldDisableOperations();
          
          // Only show quiz alert if not transitioning and course is not completed (reading mode) and operations are not disabled
          const lastTopic = sessionStorage.getItem('lastTopic');
          if(!lock && !isCourseCompleted && !disableOps && timeSpent != null && timeSpent === 0 && lastTopic === '0' && !isTransitioning){
            setQuizalert && setQuizalert(true);
          }
          
          // Don't start timer if course is completed (reading mode) or operations are disabled
          if (isCourseCompleted || disableOps) {
            // Course completed or operations disabled - show completed timer (0 remaining)
            setTimerShow(false);
            setBackRemainingTime(0);
          } else if (timeSpent && timeSpent > 0) {
            setTimerShow(true);
            setBackRemainingTime(timeSpent);
          } else if (timeSpent === 0) { 
            setTimerShow(false); 
            setBackRemainingTime(0);
          } else if (timeSpent === null) {
            setTimerShow(true);
            setBackRemainingTime(timeInSeconds);
          }
        }
      }
      
      // Clear transitioning state only after pages are successfully loaded
      // This prevents blank screen during topic transition
      if (pagesData.length > 0) {
        setIsTransitioning(false);
      }
    } catch (error) {
      console.error("Failed to fetch pages:", error);
      setHasError(true);
      // Set empty pages array on error but don't leave user with blank screen
      setPages && setPages([]);
      // Clear transitioning even on error to prevent stuck state
      setIsTransitioning(false);
    } finally {
      setIsLoadingPages(false);
      setIsInitialLoad(false); // Ensure we're not stuck in initial load
    }
  };

  let currentPage= {};
  if(currentPageIndex>0){
    currentPage = (pages || [])[currentPageIndex];
  }
  else{
    currentPage = (pages || [])[0];
  }


  // const markPagesAsRead = () => {
  //   setHasReadAllPages(true); // Mark pages as read and save to localStorage
  // };

  const  handlestart =()=>{
    // setHasStarted(true);
    const lastTopic = sessionStorage.getItem("lastTopic");
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');  // Parse user data once for easier use

    // IMPORTANT:
    // "Resume Where I Left Off" should not auto-route to the exam based on status flags.
    // Only go to exam when explicitly allowed (user clicked an "Exam" button elsewhere).
    const examAllow = sessionStorage.getItem("examAllow");
    if (examAllow === "true" || examAllow === true) {
      sessionStorage.removeItem("examAllow");
      navigate('/course/take-exam');
      return;
    }

    const currentPage = sessionStorage.getItem("currentPage");
    const topic = sessionStorage.getItem("currentTopic");
    if (lastTopic === '1') {
      setCurrentPageIndex((pages || []).length);
    } else if (currentPage) {
        setCurrentPageIndex(currentPage);
        setTopic && setTopic(JSON.parse(topic));
    }
    setStartCourse && setStartCourse(true);
    setSidebarlock && setSidebarlock(false);
    
    // Call getPages to trigger quiz alert logic when resuming
    if (topic && JSON.parse(topic).id) {
      getPages();
    }
    
  }

  const handleExit = ()=>{
    setShowCourseExitModal(false);
    setShowQuizExitModal(false); // Ensure quiz exit modal is also closed
    setDisplayback(true);
    setStartCourse && setStartCourse(false);
    setSidebarlock && setSidebarlock(false);
    
    // Reset all quiz-related states to ensure clean exit
    setStartQuiz(false);
    setShowquestion(false);
    setQuizalert && setQuizalert(false);
    setQuizbutton(false);
    setSubmitquizButton(false);
    setQuestion("");
    
    // Manually remove any remaining modal backdrops and clean up body styles
    setTimeout(() => {
      const backdrops = document.querySelectorAll('.modal-backdrop');
      backdrops.forEach(backdrop => backdrop.remove());
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }, 50);
  }


  // Derive which "screen" is currently rendered.
  // NOTE: This mirrors the render branching below so our elapsed timer runs only on allowed screens.
  const isCourseCompleted = courseStatus === '2' || (user && user.course_status === '2');
  const disableOps = shouldDisableOperations();
  const showChapterQuizPrompt =
    !disableOps &&
    !isCourseCompleted &&
    ((quizbutton && startquiz && !showquestion) ||
      (timerShow === false &&
        currentPageIndex === (pages || []).length &&
        sessionStorage.getItem('topicStatus') === '1' &&
        !startquiz &&
        !quizbutton));

  const isCourseContentVisible =
    startcourse &&
    !isTransitioning &&
    !hasError &&
    !showquestion &&
    !quizalert &&
    !showChapterQuizPrompt;

  const isChapterQuizPromptVisible =
    startcourse &&
    !isTransitioning &&
    !hasError &&
    showChapterQuizPrompt;

  const isQuizAlertVisible =
    startcourse &&
    !isTransitioning &&
    !hasError &&
    quizalert === true;

  const isSingleQuestionQuizVisible =
    startcourse &&
    !isTransitioning &&
    !hasError &&
    showquestion === true &&
    startquiz === true;

  const isElapsedAllowedScreen =
    isCourseContentVisible ||
    isChapterQuizPromptVisible ||
    isQuizAlertVisible ||
    isSingleQuestionQuizVisible;

  const shouldRunTotalTimeElapsed =
    isElapsedAllowedScreen &&
    !showChallengeModal &&
    !isCourseCompleted &&
    !disableOps;

  // Update totalTimeElapsed every second ONLY while the user is on one of these screens:
  // - CourseContentScreen
  // - ChapterQuizPromptScreen
  // - QuizAlertScreen
  // - SingleQuestionQuizScreen
  // totalTimeElapsed is standalone and is NOT tied to the topic timer.
  useEffect(() => {
    if (shouldRunTotalTimeElapsed) {
      // Convert breakpoints to numbers to ensure type safety
      const bp1 = breakpoint1 != null ? Number(breakpoint1) : null;
      const bp2 = breakpoint2 != null ? Number(breakpoint2) : null;
      
      console.log("[TotalTimeElapsed] Starting standalone timer. breakpoint1:", bp1, "breakpoint2:", bp2, "askedBreakpoints:", askedBreakpoints);
      
      const interval = setInterval(() => {
        setTotalTimeElapsed((prev) => {
          // Do not start from 0 if backend hasn't initialized yet
          if (prev === null || prev === undefined) {
            return prev;
          }
          const prevNum = Number(prev);
          if (!Number.isFinite(prevNum)) {
            return prev;
          }
          const next = prevNum + 1;
          sessionStorage.setItem("total_time_elapsed", String(next));
          console.log("[TotalTimeElapsed] Updated to:", next, "seconds | breakpoint1:", bp1, "breakpoint2:", bp2);
          
          // Check if we've crossed a breakpoint (all in seconds)
          // Check breakpoint1 - trigger when we cross or reach the breakpoint
          if (bp1 != null && 
              !askedBreakpoints.includes(bp1) &&
              prevNum < bp1 && 
              next >= bp1) {
            console.log("[Breakpoint] ✅ TRIGGERED breakpoint1! prev:", prevNum, "next:", next, "breakpoint1:", bp1);
            console.log("[Breakpoint] Conditions - bp1 != null:", bp1 != null, "!askedBreakpoints.includes(bp1):", !askedBreakpoints.includes(bp1), "prevNum < bp1:", prevNum < bp1, "next >= bp1:", next >= bp1);
            // Store current topic timer state before pausing
            if (timerShow && currentCounterTimeRef.current > 0) {
              pausedTopicTimerRef.current = currentCounterTimeRef.current;
              console.log("[Breakpoint] Stored topic timer state:", pausedTopicTimerRef.current, "seconds remaining");
            }
            // Pause timer and show challenge modal
            setTimerShow(false);
            if (setShowChallengeModal) {
              console.log("[Breakpoint] Setting showChallengeModal to true");
              setShowChallengeModal(true);
              // Force a re-render by updating sessionStorage
              sessionStorage.setItem("challenge_modal_triggered", "true");
            } else {
              console.error("[Breakpoint] ERROR: setShowChallengeModal is undefined!");
            }
            // Do NOT nullify breakpoint1. We prevent re-trigger within the same attempt using askedBreakpoints,
            // and we reset askedBreakpoints on retake when totalTimeElapsed resets to 0.
            setAskedBreakpoints(prev => {
              const updated = [...prev, bp1];
              sessionStorage.setItem("asked_breakpoints", JSON.stringify(updated));
              return updated;
            });
          } else if (bp1 != null && next >= bp1) {
            // Debug: Log why breakpoint1 didn't trigger
            console.log("[Breakpoint] breakpoint1 NOT triggered - prev:", prevNum, "next:", next, "bp1:", bp1, "askedBreakpoints:", askedBreakpoints, "includes:", askedBreakpoints.includes(bp1));
          }
          
          // Check breakpoint2 - trigger when we cross or reach the breakpoint
          if (bp2 != null && 
              !askedBreakpoints.includes(bp2) &&
              prevNum < bp2 && 
              next >= bp2) {
            console.log("[Breakpoint] ✅ TRIGGERED breakpoint2! prev:", prevNum, "next:", next, "breakpoint2:", bp2);
            console.log("[Breakpoint] Conditions - bp2 != null:", bp2 != null, "!askedBreakpoints.includes(bp2):", !askedBreakpoints.includes(bp2), "prevNum < bp2:", prevNum < bp2, "next >= bp2:", next >= bp2);
            // Store current topic timer state before pausing
            if (timerShow && currentCounterTimeRef.current > 0) {
              pausedTopicTimerRef.current = currentCounterTimeRef.current;
              console.log("[Breakpoint] Stored topic timer state:", pausedTopicTimerRef.current, "seconds remaining");
            }
            // Pause timer and show challenge modal
            setTimerShow(false);
            if (setShowChallengeModal) {
              console.log("[Breakpoint] Setting showChallengeModal to true");
              setShowChallengeModal(true);
              // Force a re-render by updating sessionStorage
              sessionStorage.setItem("challenge_modal_triggered", "true");
            } else {
              console.error("[Breakpoint] ERROR: setShowChallengeModal is undefined!");
            }
            // Do NOT nullify breakpoint2. We prevent re-trigger within the same attempt using askedBreakpoints,
            // and we reset askedBreakpoints on retake when totalTimeElapsed resets to 0.
            setAskedBreakpoints(prev => {
              const updated = [...prev, bp2];
              sessionStorage.setItem("asked_breakpoints", JSON.stringify(updated));
              return updated;
            });
          } else if (bp2 != null && next >= bp2) {
            // Debug: Log why breakpoint2 didn't trigger
            console.log("[Breakpoint] breakpoint2 NOT triggered - prev:", prevNum, "next:", next, "bp2:", bp2, "askedBreakpoints:", askedBreakpoints, "includes:", askedBreakpoints.includes(bp2));
          }
          
          return next;
        });
      }, 1000); // Every 1 second
      return () => clearInterval(interval);
    }
  }, [shouldRunTotalTimeElapsed, breakpoint1, breakpoint2, askedBreakpoints, setTotalTimeElapsed, setShowChallengeModal, timerShow, currentPageIndex, pages, quizbutton, startquiz, showquestion, quizalert, hasError, courseStatus, user]);

  // Resume timer when challenge modal is closed (if answer was correct)
  // Track previous modal state to detect when it transitions from open to closed
  const prevShowChallengeModalRef = useRef(showChallengeModal);
  
  useEffect(() => {
    // If modal was just closed (transitioned from true to false) and timer is paused
    if (prevShowChallengeModalRef.current === true && 
        showChallengeModal === false && 
        startcourse && 
        timerShow === false && 
        !lock) {
      // Only resume if we're not in a locked state (e.g., quiz, etc.)
      const shouldResume = !showquestion && !startquiz && !quizalert;
      if (shouldResume) {
        console.log("[Content] Resuming timer after challenge modal closed (correct answer)");
        // Only resume the topic timer if we actually paused it with remaining time > 0.
        // If the topic timer was already completed (remaining time 0), do NOT restart it.
        if (pausedTopicTimerRef.current !== null && pausedTopicTimerRef.current > 0) {
          setBackRemainingTime(pausedTopicTimerRef.current);
          currentCounterTimeRef.current = pausedTopicTimerRef.current;
          console.log("[Content] Restored topic timer to:", pausedTopicTimerRef.current, "seconds");
          pausedTopicTimerRef.current = null; // Clear the stored state
          setTimerShow(true);
        } else {
          console.log("[Content] Not resuming topic timer (no paused remaining time).");
        }
      }
    }
    // Update ref to current state
    prevShowChallengeModalRef.current = showChallengeModal;
  }, [showChallengeModal, startcourse, timerShow, lock, showquestion, startquiz, quizalert]);

  // Sync to backend every 30 seconds
  // Don't sync if course_status is '2' (completed/reading mode) or operations are disabled
  useEffect(() => {
    const isCourseCompleted = courseStatus === '2' || (user && user.course_status === '2');
    const disableOps = shouldDisableOperations();
    if(timerShow && startcourse && !isCourseCompleted && !disableOps){
      const interval = setInterval(() => {
        updateRemainingTime();
      }, 30000); // 30 seconds
      return () => clearInterval(interval);
    }
  }, [timerShow, startcourse, courseStatus, user]);

  // Standalone totalTimeElapsed backend sync (CourseContentScreen-only)
  useEffect(() => {
    if (!shouldRunTotalTimeElapsed) {
      return;
    }
    const token = sessionStorage.getItem("token");
    const user_id = sessionStorage.getItem("user_id");
    if (!token || !user_id) {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const elapsed = Number(sessionStorage.getItem("total_time_elapsed"));
        if (!Number.isFinite(elapsed)) {
          return;
        }
        const response = await updateTotalTimeElapsed(token, {
          user_id,
          time_elapsed: elapsed,
        });

        const updatedUser =
          response?.data?.data?.user ?? response?.data?.user ?? null;
        if (updatedUser) {
          sessionStorage.setItem("user", JSON.stringify(updatedUser));
        }
      } catch (error) {
        console.error("[TotalTimeElapsed] Error syncing elapsed time:", error);
      }
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [shouldRunTotalTimeElapsed]);

  // const updateStatusToDone = async () => {
  //   if (!props.topic || !props.topic.id) {
  //     console.error("Topic or Topic ID is undefined");
  //     return;
  //   }
  //   const token = sessionStorage.getItem("token");
  //   const user_id = sessionStorage.getItem("user_id");
  //   showQuestion(props.topic.id , user_id);

  //   // await updateTopic(token, props.topic.id, user_id);
  //   // props.setShowSuccess(true);
  //   // setTimerShow(false);
  //   // props.refreshSections();   
  // };

  const handleQuiz = async ()=>{
    // Check if operations should be disabled
    if (shouldDisableOperations()) {
      return; // Don't allow quiz if operations are disabled
    }
    
    // Set loading states immediately and synchronously to prevent any screen switching
    // This must happen first to lock the screen in place
    setQuizbutton(true);
    setStartQuiz(true);
    
    const user_id = sessionStorage.getItem("user_id");
    const topicId = props.topic.id;
    
    try {
      await showQuestion(topicId, user_id);
      // Success - states will be set in showQuestion
    } catch (error) {
      console.error("Error loading question:", error);
      // Reset all states on error - this will allow user to try again
      setQuizbutton(false);
      setStartQuiz(false);
      setShowquestion(false);
      setQuizError(true);
    }
  }

  const showQuestion = async (t_id,u_id)=>{
    // Check if operations should be disabled
    if (shouldDisableOperations()) {
      setQuizError(true);
      setQuizbutton(false);
      setStartQuiz(false);
      setShowquestion(false);
      throw new Error("Operations are disabled"); // Re-throw so handleQuiz can catch it
    }
    
    try {
      setUseranswer("");
      setQuizError(false);
      const token = sessionStorage.getItem("token");
      const response = await getQuestion(token, t_id, u_id, '0');
      setQuestion(response.data);
      setShowquestion(true);
      setTimerShow(false);
      setQuizbutton(false);
    } catch (error) {
      console.error("Error fetching question:", error);
      setQuizError(true);
      setQuizbutton(false);
      setStartQuiz(false);
      setShowquestion(false);
      throw error; // Re-throw so handleQuiz can catch it
    }
  } 

  const handleUseranswer=(e)=>{
      setUseranswer(e.target.value);
  }


  const updateRemainingTime = async () => {
    // Check if operations should be disabled
    if (shouldDisableOperations()) {
      return; // Don't update time if operations are disabled
    }
    
    const token = sessionStorage.getItem("token");
    const user_id = sessionStorage.getItem("user_id");
    const topicId = sessionStorage.getItem("currentTopicId");

    console.log("currentCounterTimeRef.current", currentCounterTimeRef.current);
    console.log("[UpdateRemainingTime] NOTE: standalone totalTimeElapsed is no longer sent with updateTopic");
    
    // Send only topic remaining time (standalone totalTimeElapsed syncs separately)
    await updateTopic(
      token,
      topicId,
      user_id,
      currentCounterTimeRef.current
    );
    // refreshSections && refreshSections(); 

    // if(currentCounterTimeRef.current == 0){
    //   props.setShowSuccess(true);
    //   setTimerShow(false);
    //   props.refreshSections(); 
    // }
  };

  const convertTimeStringToSeconds = (timeInput) => {
    // If it's already a number, assume it's in seconds and return as-is
    if (typeof timeInput === 'number' && Number.isFinite(timeInput)) {
      return timeInput;
    }
    
    // If it's a string, try to parse it
    if (typeof timeInput === 'string') {
      // Check if it's already a number string (e.g., "90")
      const numValue = Number(timeInput);
      if (!isNaN(numValue) && !timeInput.includes(':')) {
        return numValue;
      }
      
      // Otherwise, parse as "HH:MM:SS" format
      const parts = timeInput.split(":");
      if (parts.length === 3) {
        const [hours, minutes, seconds] = parts.map(Number);
        return hours * 3600 + minutes * 60 + seconds;
      }
    }
    
    // Fallback: return 0 if we can't parse it
    console.warn("[convertTimeStringToSeconds] Could not parse time input:", timeInput);
    return 0;
  };

  const handleNext = () => {
    setCurrentPageIndex((prevIndex) => {
      const newIndex = prevIndex + 1;
      sessionStorage.setItem("currentPage", newIndex);
      console.log('Next page:', newIndex, 'Total pages:', (pages || []).length);
      return newIndex;
    });
  };


 const submitQuiz = async () => {
  // Check if operations should be disabled
  if (shouldDisableOperations()) {
    return; // Don't allow quiz submission if operations are disabled
  }
  
  setQuizalert && setQuizalert(false);
  const token = sessionStorage.getItem("token");
  const user_id = sessionStorage.getItem("user_id");
  
  if (useranswer) {
    setSubmitquizButton(true);
    setQuizError(false);
    setIsTransitioning(true); // Set transitioning state to prevent flash
    
    let response;
    
    // Use different API based on whether it's the last chapter of the last section
    if (isLastChapterOfLastSection) {
      // Call submit-course API for the last chapter
      response = await submitCourse(token, topic.id, user_id, useranswer, question.id);
    } else {
      // Call regular update-topic-status API for other chapters
      response = await updateTopicStatus(token, topic.id, user_id, useranswer, question.id);
    }
    
    if (response) {
      setShowSuccess && setShowSuccess(true);
      setTimerShow(false); // Ensure timer stops during processing
      setStartQuiz(false);
      setShowquestion(false);
      sessionStorage.setItem('user', JSON.stringify(response.data.user));
      
      refreshSections && refreshSections();  
      sessionStorage.setItem('topicStatus', '2');
      setQuestion("");
      setSubmitquizButton(false);

      if (isLastChapterOfLastSection) {
        // Course completed - navigate to results page
        sessionStorage.removeItem("topicStatus");
        sessionStorage.removeItem("lastTopic");
        sessionStorage.setItem("user", JSON.stringify(response.data.user));
        sessionStorage.setItem("testResult", response.data.score);
        sessionStorage.setItem("total", response.data.total);
        sessionStorage.setItem("total_questions", response.data.questions);
        setDisplayback(true);
        navigate("/course/scoreboard");
      } else {
        // Determine the next topic to navigate to
        const currentSectionIndex = sections.findIndex(
          (section) => section.course_section_id === topic.course_section_id
        );

        if (currentSectionIndex >= 0) {
          const currentSection = sections[currentSectionIndex];
          const topicIndex = currentSection.topics.findIndex((topic) => topic.status === "1");
          let nextTopic = topicIndex >= 0 && topicIndex + 1 < currentSection.topics.length
            ? currentSection.topics[topicIndex + 1]
            : null;

          if (!nextTopic) {
            const nextSection = sections[currentSectionIndex + 1];
            if (nextSection) {
              nextTopic = nextSection.topics[0];
            }
          }

          if (nextTopic) {
            setTopic && setTopic(nextTopic.topics);
            sessionStorage.setItem("currentTopic", JSON.stringify(nextTopic.topics));
            sessionStorage.setItem("currentTopicId", nextTopic.topics.id);
            sessionStorage.setItem("topicStatus", "1");
            // Don't set pages here - they'll be fetched via getPages() when topic changes

            setTimerShow(true); // Start the timer for the next topic
            setBackRemainingTime(convertTimeStringToSeconds(nextTopic.topics.time)); // Set initial time
            setCurrentPageIndex(0);
            
            // Keep isTransitioning true - it will be cleared in getPages() after pages are loaded
            // This prevents blank screen during the transition
          } else {
            // No more topics - course completed, navigate to results
            sessionStorage.removeItem("topicStatus");
            sessionStorage.removeItem("lastTopic");
            sessionStorage.setItem("user", JSON.stringify(response.data.user));
            sessionStorage.setItem("testResult", response.data.score);
            sessionStorage.setItem("total", response.data.total);
            setDisplayback(true);
            navigate("/course/scoreboard");
          }
        }
      }
    } else {
      setStartQuiz(false);
      setShowquestion(false);
      getPages();
      setSubmitquizButton(false);
      setIsTransitioning(false);
    }
  } else {
    setQuizError(true);
  }
};



  // const handleFinalQuiz = ()=>{
  //   const user_id = sessionStorage.getItem("user_id");
  //   showquestions(props.topic.id , user_id);
  //   setStartQuiz(true);
  // }

  // const showquestions=async(t_id, u_id)=>{
  //     const token = sessionStorage.getItem("token");
  //     const response = await getFinalquestions(token,t_id, u_id);
  //     setFinalquestions(response.data);
  // }

  const handlePrevious = () => {
    setCurrentPageIndex((prevIndex) => {
      const newIndex = Math.max(0, prevIndex - 1); // Ensure we don't go below 0
      sessionStorage.setItem("currentPage", newIndex);
      console.log('Previous page:', newIndex, 'Total pages:', (pages || []).length);
      return newIndex;
    });
  };

  const handlecompleteread=()=>{
    setDisplayback(true);
  }

  const readchapter =()=>{
    setStartCourse && setStartCourse(true);
    setStartQuiz(false);
    setShowquestion(false);
    setQuizalert && setQuizalert(false);
    setSidebarlock && setSidebarlock(false);
    setQuestion("");
    setCurrentPageIndex(0);
    setSubmitquizButton(false);
  }


  const cancelQuiz=()=>{
    setShowQuizExitModal(false);
    setStartQuiz(false);
    setShowquestion(false);
    setQuestion("");
    setCurrentPageIndex((pages || []).length-1);
    setSubmitquizButton(false);
    setQuizbutton(false); // Reset loading state
  }

  
  if(startquiz && showquestion){
    return (
      <>
        <SingleQuestionQuizScreen
          question={question}
          handleUseranswer={handleUseranswer}
          submitQuiz={submitQuiz}
          cancelQuiz={cancelQuiz}
          quizerror={quizerror}
          submitquizbutton={submitquizbutton}
          currentquiztime={currentquiztime}
          setCurrentquiztime={setCurrentquiztime}
          setShowQuizExitModal={setShowQuizExitModal}
        />
        
        {/* Confirmation Modals */}
        {showQuizExitModal && (
          <div className="modal fade show d-block" style={{zIndex: 1050}} tabIndex="-1" aria-labelledby="quizExitModalLabel" aria-hidden="false">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="quizExitModalLabel">Exit Quiz</h5>
                  <button type="button" className="btn-close" onClick={() => setShowQuizExitModal(false)} aria-label="Close"></button>
                </div>
                <div className="modal-body">
                  <strong>Warning:</strong> If you exit now, all your progress will be lost and you will need to restart the quiz from the beginning.
                  <br /><br />
                  Are you sure you want to exit?
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowQuizExitModal(false)}>Cancel</button>
                  <button type="button" className="btn btn-danger" onClick={cancelQuiz}>Exit Quiz</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal backdrop */}
        {showQuizExitModal && (
          <div className="modal-backdrop fade show" style={{zIndex: 1040}}></div>
        )}
      </>
    );
  }
  if(quizalert && startcourse && !isTransitioning){
    return (
      <QuizAlertScreen
        readchapter={readchapter}
        handleQuiz={handleQuiz}
        quizbutton={quizbutton}
      />
    );
  }
 
  // Show error state if there's an error
  if (hasError) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <h5 className="text-danger">Error loading content</h5>
          <p>There was an error loading the course content. Please try refreshing the page.</p>
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setHasError(false);
              if (topic && topic.id) {
                getPages();
              }
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <>
      {startcourse ? (
        <>
          <div className="position-relative">
            {/* Blur effect for content when loading */}
            <div style={{ 
              filter: (isTransitioning || isInitialLoad || isLoadingPages) ? 'blur(3px)' : 'none',
              transition: 'filter 0.3s ease-in-out'
            }}>
            
            {/* Show ChapterQuizPromptScreen when: 
                1. When quiz is loading (ABSOLUTE PRIORITY - must check this first to prevent screen switching)
                   This condition MUST be checked independently and take precedence over everything
                2. OR normal conditions (timer off, at end of pages, topic status is 1, and NOT loading)
                
                CRITICAL: Loading state check is completely independent - if loading, show prompt screen
                regardless of ANY other state (timerShow, currentPageIndex, pages, etc.) */}
            {/* Don't show quiz prompt if course_status is '2' (completed/reading mode) or operations are disabled */}
            {!shouldDisableOperations() && 
             !((user && user.course_status === '2') || courseStatus === '2') && 
             ((quizbutton && startquiz && !showquestion) || 
             (timerShow === false && 
             currentPageIndex === (pages || []).length && 
             sessionStorage.getItem('topicStatus') === '1' && 
             !startquiz && !quizbutton)) ? (
              <ChapterQuizPromptScreen
                handleQuiz={handleQuiz}
                handleExit={handleExit}
                cancelQuiz={cancelQuiz}
                quizbutton={quizbutton}
                setShowCourseExitModal={setShowCourseExitModal}
                setShowQuizExitModal={setShowQuizExitModal}
              />
            ) : (
              <CourseContentScreen
                topic={topic}
                currentPage={currentPage}
                currentPageIndex={currentPageIndex}
                pages={pages}
                timerShow={timerShow}
                setTimerShow={setTimerShow}
                backremainingTime={backremainingTime}
                totalTime={totalTime}
                currentCounterTimeRef={currentCounterTimeRef}
                setCurrentPageIndex={setCurrentPageIndex}
                handlePrevious={handlePrevious}
                handleNext={handleNext}
                handleExit={handleExit}
                updateRemainingTime={updateRemainingTime}
                lock={lock}
                showChallengeModal={showChallengeModal}
                courseStatus={courseStatus}
              />
            )}
            
            {/* Fallback content if nothing else renders */}
            {(!currentPage || !pages || pages.length === 0) && 
             !isTransitioning && 
             !isLoadingPages && 
             !isInitialLoad && (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                  <h5>Loading content...</h5>
                  <p>Please wait while we load the course content.</p>
                </div>
              </div>
            )}
            </div>
            
            {/* Loading overlay */}
            {(isTransitioning || isInitialLoad || isLoadingPages) && (
              <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" 
                   style={{ 
                     backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                     zIndex: 1000,
                     backdropFilter: 'blur(2px)'
                   }}>
                <LoadingScreen />
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="d-flex justify-content-center">
          {displayback ? (
            <WelcomeBackScreen
              handlestart={handlestart}
              readchapter={readchapter}
            />
          ) : (() => {
            try {
              const user = JSON.parse(sessionStorage.getItem('user'));
              return user.course_status === '2' ? (
                <CourseLockedScreen readchapter={readchapter} />
              ) : (
                <InitialWelcomeScreen handlestart={handlestart} />
              );
            } catch (error) {
              console.error('Error parsing user data:', error);
              return <InitialWelcomeScreen handlestart={handlestart} />;
            }
          })()}
        </div>
      )}

      {/* Confirmation Modals - only show quiz exit modal when actually in quiz */}
      {showQuizExitModal && startcourse && (startquiz || showquestion) && (
        <div className="modal fade show d-block" style={{zIndex: 1050}} tabIndex="-1" aria-labelledby="quizExitModalLabel" aria-hidden="false">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="quizExitModalLabel">Exit Quiz</h5>
                <button type="button" className="btn-close" onClick={() => setShowQuizExitModal(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <strong>Warning:</strong> If you exit now, all your progress will be lost and you will need to restart the quiz from the beginning.
                <br /><br />
                Are you sure you want to exit?
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowQuizExitModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={cancelQuiz}>Exit Quiz</button>
              </div>
            </div>
          </div>
        </div>
      )}


      {showCourseExitModal && (
        <div className="modal fade show d-block" style={{zIndex: 1050}} tabIndex="-1" aria-labelledby="courseExitModalLabel" aria-hidden="false">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="courseExitModalLabel">Exit Course</h5>
                <button type="button" className="btn-close" onClick={() => setShowCourseExitModal(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                You will need to resume from the beginning of this Chapter if you Exit.
                <br /><br />
                Are you sure you want to exit?
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCourseExitModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleExit}>Exit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal backdrop */}
      {(showQuizExitModal || showCourseExitModal) && (
        <div className="modal-backdrop fade show" style={{zIndex: 1040}}></div>
      )}
    </>
  );

}
export default Content;
